/**
 * NormalizedProductImage — content-size-normalized product thumbnail
 * (owner QA 07-06: in the comparison table, a product photographed with lots
 * of padding rendered visually smaller than its neighbors).
 *
 * How: after load, downsample to a 64px canvas, find the bounding box of
 * non-white/non-transparent pixels, and scale the image so the CONTENT fills
 * ~92% of the box — every column's product reads the same size regardless of
 * how much whitespace the source photo carries.
 *
 * Safety/perf: the image is fetched through our same-origin /_next/image
 * proxy so the canvas is never tainted; analysis is one 64×64 read per
 * image. Any failure (tainted canvas, decode error) leaves scale at 1 —
 * graceful degradation to plain object-contain.
 */

'use client'

import { useRef, useState } from 'react'
import { NoImageFallback } from '@/components/ui/no-image-fallback'
import { canOptimizeImageSrc } from '@/lib/image-optimizable'

interface NormalizedProductImageProps {
  src: string
  alt: string
  /** Sizing/border classes for the box (the img normalizes inside it). */
  boxClass?: string
}

const ANALYSIS_SIZE = 64
const TARGET_FILL = 0.92
const MAX_SCALE = 1.8

export function NormalizedProductImage({ src, alt, boxClass = '' }: NormalizedProductImageProps) {
  const imgRef = useRef<HTMLImageElement>(null)
  const [scale, setScale] = useState(1)
  const [failed, setFailed] = useState(false)

  const onLoad = () => {
    try {
      const img = imgRef.current
      if (!img || !img.naturalWidth) return
      const c = document.createElement('canvas')
      c.width = ANALYSIS_SIZE
      c.height = ANALYSIS_SIZE
      const ctx = c.getContext('2d', { willReadFrequently: true })
      if (!ctx) return
      ctx.drawImage(img, 0, 0, ANALYSIS_SIZE, ANALYSIS_SIZE)
      const d = ctx.getImageData(0, 0, ANALYSIS_SIZE, ANALYSIS_SIZE).data
      let minX = ANALYSIS_SIZE
      let minY = ANALYSIS_SIZE
      let maxX = -1
      let maxY = -1
      for (let y = 0; y < ANALYSIS_SIZE; y++) {
        for (let x = 0; x < ANALYSIS_SIZE; x++) {
          const i = (y * ANALYSIS_SIZE + x) * 4
          if (d[i + 3] < 10) continue // transparent
          if (d[i] > 245 && d[i + 1] > 245 && d[i + 2] > 245) continue // near-white bg
          if (x < minX) minX = x
          if (x > maxX) maxX = x
          if (y < minY) minY = y
          if (y > maxY) maxY = y
        }
      }
      if (maxX < 0) return // blank/all-white — leave alone
      const contentFrac = Math.max(maxX - minX + 1, maxY - minY + 1) / ANALYSIS_SIZE
      if (contentFrac > 0 && contentFrac < TARGET_FILL) {
        setScale(Math.min(MAX_SCALE, TARGET_FILL / contentFrac))
      }
    } catch {
      // Tainted canvas / decode quirk → plain object-contain.
    }
  }

  if (failed || !src) {
    // FRIDGE-239 (08/03/2026): the empty slot now carries the Fridge.com mark.
    // The earlier owner note of 07/06 ruled out an EMOJI placeholder; it did
    // not choose a bare box over the brand mark, and the 08/03 direction
    // supersedes it either way.
    //
    // `failed` is included deliberately: an image that 404s at the retailer
    // leaves the same empty slot as one that was never set, and the user
    // cannot tell the difference. This is the surface FRIDGE-205 measures at
    // 81.6% of live product pages carrying one image or none, so it is the
    // slot most often empty on the site.
    return (
      <div className={boxClass}>
        <NoImageFallback />
      </div>
    )
  }

  // Same-origin proxy: keeps the canvas readable and the visible host ours.
  // Stored photos go through it too (FRIDGE-1506 D4: /product-images/ files
  // up to 1,600 px were drawn in these 128-160 px boxes); a stored path the
  // optimizer refuses (a ?v= query, an SVG) stays as it is.
  const local = src.startsWith('/') && !src.startsWith('//')
  const proxy = (w: number) => `/_next/image?url=${encodeURIComponent(src)}&w=${w}&q=75`
  const optimized = !local || (canOptimizeImageSrc(src) && !/\.svg$/i.test(src))
  const proxied = optimized ? proxy(256) : src
  const srcSet = optimized ? `${proxy(256)} 256w, ${proxy(384)} 384w` : undefined

  return (
    <div className={`overflow-hidden flex items-center justify-center ${boxClass}`}>
      {/* eslint-disable-next-line @next/next/no-img-element */}
      {/* FRIDGE-254 done-when #6: intrinsic dimensions on every product
          <img>. The box (boxClass) is explicitly sized (w-32/w-40), so page
          layout never shifted — the attributes only size the element inside
          the box before load instead of leaving it unsized, and
          max-w/max-h still cap it to the box. 160 = the box's largest
          rendering (md:w-40). */}
      <img
        ref={imgRef}
        src={proxied}
        srcSet={srcSet}
        sizes={srcSet ? '(min-width: 768px) 160px, 128px' : undefined}
        alt={alt}
        width={160}
        height={160}
        loading="lazy"
        onLoad={onLoad}
        onError={() => setFailed(true)}
        className="max-w-full max-h-full object-contain"
        // FRIDGE-1540: the img fills the fixed box (object-contain draws the photo the same),
        // so Chromium counts this lazy image as sized.
        style={{ width: '100%', height: '100%', transform: `scale(${scale})`, transformOrigin: 'center' }}
      />
    </div>
  )
}

export default NormalizedProductImage
